import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Close menu when route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  // ── Links based on role
  const customerLinks = [
    { label: "🏪 Salons", path: "/shops" },
    { label: "📅 My Bookings", path: "/my-bookings" },
  ];
  const barberLinks = [
    { label: "📊 Dashboard", path: "/dashboard" },
    { label: "🎟️ Coupons", path: "/coupons" },
  ];
  const guestLinks = [
    { label: "🏪 Browse Salons", path: "/shops" },
  ];

  const links = !user ? guestLinks : user.role === "barber" ? barberLinks : customerLinks;

  return (
    <>
      <nav style={styles.nav}>
        <div style={styles.inner}>

          {/* ── Logo ── */}
          <div
            style={styles.logo}
            onClick={() => goTo(user?.role === "barber" ? "/dashboard" : "/shops")}
          >
            <span style={styles.logoIcon}>💈</span>
            <span style={styles.logoText}>TrimSpot</span>
          </div>

          {/* ── Desktop Links (hidden on mobile) ── */}
          {!isMobile && user?.role !== "barber" && (
            <div style={styles.desktopLinks}>
              {links.map((link) => (
                <button
                  key={link.path}
                  onClick={() => goTo(link.path)}
                  style={{
                    ...styles.navLink,
                    ...(isActive(link.path) ? styles.navLinkActive : {}),
                  }}
                >
                  {link.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Desktop Right (hidden on mobile) ── */}
          {!isMobile && (
            <div style={styles.desktopRight}>
              {!user ? (
                <>
                  <button onClick={() => goTo("/login")} style={styles.loginBtn}>Login</button>
                  <button onClick={() => goTo("/register")} style={styles.registerBtn}>Register Free</button>
                </>
              ) : (
                <>
                  <span style={styles.userName}>👋 {user.name?.split(" ")[0]}</span>
                  <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
                </>
              )}
            </div>
          )}

          {/* ── Mobile Right (user name + hamburger) ── */}
          {isMobile && (
            <div style={styles.mobileRight}>
              {user && (
                <span style={styles.mobileUserName}>
                  👋 {user.name?.split(" ")[0]}
                </span>
              )}
              <button
                style={styles.hamburger}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? "✕" : "☰"}
              </button>
            </div>
          )}

        </div>

        {/* ── Mobile Dropdown Menu ── */}
        {isMobile && menuOpen && (
          <div style={styles.mobileMenu}>

            {/* Links */}
            {links.map((link) => (
              <button
                key={link.path}
                onClick={() => goTo(link.path)}
                style={{
                  ...styles.mobileLink,
                  ...(isActive(link.path) ? styles.mobileLinkActive : {}),
                }}
              >
                {link.label}
              </button>
            ))}

            {/* Divider */}
            <div style={styles.mobileDivider} />

            {/* Auth buttons */}
            {!user ? (
              <>
                <button onClick={() => goTo("/login")} style={styles.mobileLoginBtn}>
                  🔑 Login
                </button>
                <button onClick={() => goTo("/register")} style={styles.mobileRegisterBtn}>
                  📝 Register Free
                </button>
              </>
            ) : (
              <button onClick={handleLogout} style={styles.mobileLogoutBtn}>
                🚪 Logout
              </button>
            )}

          </div>
        )}
      </nav>

      {/* ── Spacer so content doesn't go behind fixed navbar ── */}
      <div style={{ height: "60px" }} />
    </>
  );
}

const styles = {
  nav: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    zIndex: 1000,
    background: "var(--bg-secondary)",
    borderBottom: "1px solid var(--border)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
  },
  inner: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "0 16px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },

  // Logo
  logo: {
    display: "flex", alignItems: "center",
    gap: "8px", cursor: "pointer", flexShrink: 0,
  },
  logoIcon: { fontSize: "22px" },
  logoText: {
    fontSize: "1.2rem", fontWeight: "900",
    color: "white", letterSpacing: "-0.5px",
  },

  // Desktop links
  desktopLinks: {
    display: "flex", gap: "4px",
    alignItems: "center", flex: 1,
    justifyContent: "center",
  },
  navLink: {
    background: "none", border: "none",
    color: "var(--text-secondary)",
    cursor: "pointer", fontSize: "13px",
    fontWeight: "500", padding: "8px 12px",
    borderRadius: "8px",
  },
  navLinkActive: {
    background: "rgba(233,69,96,0.1)",
    color: "var(--accent)", fontWeight: "bold",
  },

  // Desktop right
  desktopRight: {
    display: "flex", gap: "8px",
    alignItems: "center", flexShrink: 0,
  },
  userName: {
    color: "var(--text-muted)", fontSize: "13px",
    whiteSpace: "nowrap",
  },
  loginBtn: {
    background: "none", border: "1px solid var(--border)",
    color: "var(--text-secondary)", padding: "7px 14px",
    borderRadius: "8px", cursor: "pointer", fontSize: "13px",
  },
  registerBtn: {
    background: "var(--accent)", border: "none",
    color: "white", padding: "7px 14px",
    borderRadius: "8px", cursor: "pointer",
    fontSize: "13px", fontWeight: "bold",
  },
  logoutBtn: {
    background: "rgba(233,69,96,0.1)",
    border: "1px solid rgba(233,69,96,0.3)",
    color: "var(--accent)", padding: "7px 14px",
    borderRadius: "8px", cursor: "pointer", fontSize: "13px",
  },

  // Mobile right (name + hamburger)
  mobileRight: {
    display: "flex", alignItems: "center", gap: "10px",
  },
  mobileUserName: {
    color: "var(--text-muted)", fontSize: "12px",
    whiteSpace: "nowrap",
  },
  hamburger: {
    background: "none", border: "1px solid var(--border)",
    color: "white", fontSize: "18px", cursor: "pointer",
    padding: "6px 10px", borderRadius: "8px",
  },

  // Mobile dropdown
  mobileMenu: {
    background: "var(--bg-primary)",
    borderTop: "1px solid var(--border)",
    padding: "12px 16px",
    display: "flex", flexDirection: "column", gap: "8px",
  },
  mobileLink: {
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
    padding: "13px 16px", borderRadius: "8px",
    cursor: "pointer", fontSize: "14px",
    textAlign: "left", fontWeight: "500",
    width: "100%", boxSizing: "border-box",
  },
  mobileLinkActive: {
    background: "rgba(233,69,96,0.1)",
    border: "1px solid rgba(233,69,96,0.3)",
    color: "var(--accent)", fontWeight: "bold",
  },
  mobileDivider: {
    height: "1px", background: "var(--border)",
    margin: "4px 0",
  },
  mobileLoginBtn: {
    background: "none", border: "1px solid var(--border)",
    color: "var(--text-secondary)", padding: "13px 16px",
    borderRadius: "8px", cursor: "pointer",
    fontSize: "14px", textAlign: "left",
    width: "100%", boxSizing: "border-box",
  },
  mobileRegisterBtn: {
    background: "var(--accent)", border: "none",
    color: "white", padding: "13px 16px",
    borderRadius: "8px", cursor: "pointer",
    fontSize: "14px", fontWeight: "bold",
    textAlign: "left",
    width: "100%", boxSizing: "border-box",
  },
  mobileLogoutBtn: {
    background: "rgba(233,69,96,0.1)",
    border: "1px solid rgba(233,69,96,0.3)",
    color: "var(--accent)", padding: "13px 16px",
    borderRadius: "8px", cursor: "pointer",
    fontSize: "14px", textAlign: "left",
    width: "100%", boxSizing: "border-box",
  },
};